import {Injectable} from '@angular/core';
import {Action, State, StateContext} from '@ngxs/store';
import {CommandStatus, CommandStatusType, CommandType} from '../../models/command-response';
import {PanelOutput} from '../../models/panel-output.interface';
import {PanelOutputsQueryResult} from '../../models/panel-outputs-query-result.interface';
import {PanelOutputsService} from '../../services/panel-outputs.service';
import {
  ChangePanelOutputsStatus,
  ClosePanelOutputGroup,
  ClosePanelOutputs,
  LoadUserPanelOutputs,
  OpenMomentOutputGroup,
  OpenMomentOutputs,
  OpenPermanentOutputGroup,
  OpenPermanentOutputs,
  ResetCommandsMessages,
  SearchUserPanelOutputs,
  SelectPanelOutput,
  SelectPanelOutputGroup,
  SetCommandError,
  SetCommandFailure,
  SetCommandPending,
  SetCommandSuccess
} from './panel-outputs.actions';
import {PanelOutputsStateModel} from './panel-outputs.model';

@State<PanelOutputsStateModel>({
  name: 'panelOutputs',
  defaults: {
    userPanelOutputs: null,
    error: null,
    selectedPanelOutput: null,
    selectedPanelOutputGroup: null,
    searchTerm: '',
    statusChangeConnection: null,
    commandStatus: {message: '', status: CommandStatusType.NotInitiated, type: null}
  }
})
@Injectable()
export class PanelOutputsState {
  constructor(private readonly panelOutputsService: PanelOutputsService) {
  }

  private static isOutputsEquel(oldOutput: PanelOutput, newOutput: PanelOutput): boolean {
    return oldOutput.id === newOutput.id && oldOutput.status === newOutput.status;
  }

  @Action(LoadUserPanelOutputs)
  async loadDevices(ctx: StateContext<PanelOutputsStateModel>) {
    await this.panelOutputsService.getUserPanelOutputs('').then((data: PanelOutputsQueryResult) => {
      if(data){
        ctx.patchState({userPanelOutputs: data.outputs});
      }
    }).catch(err => ctx.patchState({error: err}));
  }

  @Action(SearchUserPanelOutputs)
  searchDevices(ctx: StateContext<PanelOutputsStateModel>, action: SearchUserPanelOutputs) {
    ctx.patchState({searchTerm: action.payload});
  }

  @Action(SelectPanelOutput)
  setSelectedItem(ctx: StateContext<PanelOutputsStateModel>, action: SelectPanelOutput) {
    let updatedSelectedOutput = ctx.getState().selectedPanelOutput;
    if (!updatedSelectedOutput) {
      updatedSelectedOutput = action.payload;
    } else {
      const oldOutput = updatedSelectedOutput;
      updatedSelectedOutput = PanelOutputsState.isOutputsEquel(oldOutput, action.payload) ? null : action.payload;
    }
    ctx.patchState({selectedPanelOutput: updatedSelectedOutput, selectedPanelOutputGroup: null});
  }

  @Action(SelectPanelOutputGroup)
  selectGroup(ctx: StateContext<PanelOutputsStateModel>, action: SelectPanelOutputGroup) {
    ctx.setState({...ctx.getState(), selectedPanelOutput: null, selectedPanelOutputGroup: action.payload});
    ctx.patchState({selectedPanelOutputGroup: action.payload, selectedPanelOutput: null});
  }

  @Action(ClosePanelOutputGroup)
  closeOutputGroup(ctx: StateContext<PanelOutputsStateModel>, action: ClosePanelOutputGroup) {
    const currentGroup = ctx.getState().selectedPanelOutputGroup;
    currentGroup.isLocking = true;
    ctx.patchState({selectedPanelOutputGroup: currentGroup});
    ctx.dispatch(new ClosePanelOutputs(action.payload.unlockedOutputs));
  }

  @Action(OpenMomentOutputGroup)
  openMomentOutputGroup(ctx: StateContext<PanelOutputsStateModel>, action: OpenMomentOutputGroup) {
    const currentGroup = ctx.getState().selectedPanelOutputGroup;
    currentGroup.isUnlocking = true;
    ctx.dispatch(new OpenMomentOutputs(action.payload.lockedOutputs));
  }

  @Action(OpenPermanentOutputGroup)
  openPermanentOutputGroup(ctx: StateContext<PanelOutputsStateModel>, action: OpenPermanentOutputGroup) {
    const currentGroup = ctx.getState().selectedPanelOutputGroup;
    currentGroup.isUnlocking = true;
    ctx.dispatch(new OpenPermanentOutputs(action.payload.lockedOutputs));
  }

  @Action(ClosePanelOutputs)
  updateRequestedOuputsLockingStatus(ctx: StateContext<PanelOutputsStateModel>, action: ClosePanelOutputs) {
    const currentOutputs = [...ctx.getState().userPanelOutputs];
    action.payload.forEach(outputToUpdate => {
      const outputIndex = currentOutputs.findIndex(output => output.id === outputToUpdate.id);
      if (outputIndex !== -1) {
        currentOutputs[outputIndex].isLocking = true;
      }
    });
    ctx.patchState({userPanelOutputs: currentOutputs});
  }

  @Action(ClosePanelOutputs)
  closeOutputs(ctx: StateContext<PanelOutputsStateModel>, action: ClosePanelOutputs) {
    const ids = this.getOutputsIdsForAction(action.payload);

    this.panelOutputsService.closeOutputs(ids)
      .then(value => ctx.dispatch(new SetCommandPending(CommandType.Lockdown)))
      .catch(() => {
        this.resetRequests(ctx, action.payload);
        ctx.dispatch(new SetCommandError(CommandType.Lockdown));
      });
  }

  @Action(OpenMomentOutputs)
  @Action(OpenPermanentOutputs)
  updateRequestedOuputsUnlockingStatus(ctx: StateContext<PanelOutputsStateModel>, action: ClosePanelOutputs) {
    const currentOutputs = [...ctx.getState().userPanelOutputs];

    action.payload.forEach(outputToUpdate => {
      const outputIndex = currentOutputs.findIndex(output => output.id === outputToUpdate.id);
      if (outputIndex !== -1) {
        currentOutputs[outputIndex].isUnlocking = true;
      }
    });

    ctx.patchState({userPanelOutputs: currentOutputs});
  }

  @Action(OpenMomentOutputs)
  openMomentOutputs(ctx: StateContext<PanelOutputsStateModel>, action: OpenMomentOutputs) {
    const ids = this.getOutputsIdsForAction(action.payload);

    this.panelOutputsService.openMomentOutputs(ids)
      .then(value => ctx.dispatch(new SetCommandPending(CommandType.Release)))
      .catch(() => this.errorHandler(ctx, action));
  }

  @Action(OpenPermanentOutputs)
  openPermanentOutputs(ctx: StateContext<PanelOutputsStateModel>, action: OpenPermanentOutputs) {
    const ids = this.getOutputsIdsForAction(action.payload);

    this.panelOutputsService.openPermanentOutputs(ids)
      .then(value => ctx.dispatch(new SetCommandPending(CommandType.Release)))
      .catch(() => this.errorHandler(ctx, action));
  }

  @Action(SetCommandError)
  setCommandError(ctx: StateContext<PanelOutputsStateModel>, action: SetCommandError) {
    const commanddMessage = action.commandType === CommandType.Lockdown ? 'error locking' : 'error unlocking';
    const newCommandStatus: CommandStatus = {
      message: commanddMessage,
      status: CommandStatusType.Error,
      type: action.commandType
    };
    ctx.patchState({commandStatus: newCommandStatus});
  }

  @Action(SetCommandPending)
  setCommandPending(ctx: StateContext<PanelOutputsStateModel>, action: SetCommandPending) {
    const commanddMessage = action.commandType === CommandType.Lockdown ? 'requested locking' : 'requested unlocking';
    const newCommandStatus: CommandStatus = {
      message: commanddMessage,
      status: CommandStatusType.Pending,
      type: action.commandType
    };
    ctx.patchState({commandStatus: newCommandStatus});
  }

  @Action(SetCommandSuccess)
  setCommandSuccess(ctx: StateContext<PanelOutputsStateModel>, action: SetCommandSuccess) {
    const commanddMessage = action.commandType === CommandType.Lockdown ? 'success locking' : 'success unlocking';
    const newCommandStatus: CommandStatus = {
      message: commanddMessage,
      status: CommandStatusType.Success,
      type: action.commandType
    };
    ctx.patchState({commandStatus: newCommandStatus});
  }

  @Action(SetCommandFailure)
  setUnlockingFailure(ctx: StateContext<PanelOutputsStateModel>, action: SetCommandFailure) {
    const commanddMessage = action.commandType === CommandType.Lockdown ? 'failed locking' : 'failed unlocking';
    const newCommandStatus: CommandStatus = {
      message: commanddMessage,
      status: CommandStatusType.Failure,
      type: action.commandType
    };
    ctx.patchState({commandStatus: newCommandStatus});
  }

  @Action(ResetCommandsMessages)
  ResetCommandsMessages(ctx: StateContext<PanelOutputsStateModel>) {
    const newCommandStatus: CommandStatus = {message: '', status: CommandStatusType.NotInitiated, type: null};
    ctx.patchState({commandStatus: newCommandStatus});
  }

  @Action(ChangePanelOutputsStatus)
  updatePanelOutputs(ctx: StateContext<PanelOutputsStateModel>, action: ChangePanelOutputsStatus) {
    if (!action.payload) {
      return;
    }
    if (ctx.getState().userPanelOutputs) {
      this.resetRequests(ctx, action.payload.Outputs);
      const currentOutputs = [...ctx.getState().userPanelOutputs];
      action.payload.Outputs.forEach(updatedOutput => {
        const outputIndex = currentOutputs.findIndex(output => output.id === updatedOutput.id);
        if (outputIndex !== -1) {
          currentOutputs[outputIndex].status = updatedOutput.status;
          currentOutputs[outputIndex].isLockdown = updatedOutput.isLockdown;
        }
      });

      ctx.patchState({userPanelOutputs: currentOutputs});
    }
  }

  @Action(ChangePanelOutputsStatus)
  updateSelectedPanelOutput(ctx: StateContext<PanelOutputsStateModel>, action: ChangePanelOutputsStatus) {
    const currentSelectedOutput = ctx.getState().selectedPanelOutput;
    if (currentSelectedOutput) {
      currentSelectedOutput.isUnlocking = false;
      action.payload.Outputs.forEach(output => {
        if (output.id === currentSelectedOutput.id) {
          currentSelectedOutput.status = output.status;
          currentSelectedOutput.isLockdown = output.isLockdown;
        }
      });
      ctx.patchState({selectedPanelOutput: currentSelectedOutput});
    }
  }

  private getOutputsIdsForAction(outputs: PanelOutput[]): string[] {
    const ids: string[] = [];
    outputs.forEach(output => ids.push(output.id.toString()));
    return ids;

  }

  private resetRequests(ctx: StateContext<PanelOutputsStateModel>, outputs: PanelOutput[]) {
    const currentGroup = ctx.getState().selectedPanelOutputGroup;
    if (currentGroup) {
      currentGroup.isLocking = false;
      currentGroup.isUnlocking = false;
    }
    const currentOutputs = [...ctx.getState().userPanelOutputs];
    outputs.forEach(outputToUpdate => {
      const outputIndex = currentOutputs.findIndex(output => output.id === outputToUpdate.id);
      if (outputIndex !== -1) {
        currentOutputs[outputIndex].isUnlocking = false;
        currentOutputs[outputIndex].isLocking = false;
      }
    });
    ctx.dispatch(new ResetCommandsMessages());
    ctx.patchState({userPanelOutputs: currentOutputs});
  }

  private errorHandler(ctx, action) {
    this.resetRequests(ctx, action.payload);

    ctx.dispatch(new SetCommandError(CommandType.Release));
  }
}
